import { Tags } from "lucide-react";
import type { Product, ProductVariant } from "@sakura-tech-lab/dashboard-api";

import { Badge } from "@/components/ui/badge";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { cn } from "@/lib/utils";
import type { AttributeSchema } from "@/services/category.service";
import { getSpecificationEntries } from "./product-detail-utils";

interface ProductSpecificationsCardProps {
  readonly product: Product;
  readonly selectedVariant: ProductVariant | null;
  readonly language: string;
  readonly attributeSchema?: AttributeSchema;
}

/**
 * Translated attributes for the selected variant, shown as a full-width row on
 * the product detail page.
 */
export function ProductSpecificationsCard({
  product,
  selectedVariant,
  language,
  attributeSchema,
}: ProductSpecificationsCardProps) {
  const specifications = getSpecificationEntries(product, selectedVariant, language, attributeSchema);
  if (specifications.length === 0) return null;

  return (
    <Card className="min-w-0">
      <CardHeader className="p-5 pb-3 sm:p-6 sm:pb-3">
        <div className="flex flex-wrap items-start justify-between gap-2">
          <div className="space-y-1">
            <CardTitle className="flex items-center gap-2 text-lg">
              <span className="flex h-7 w-7 items-center justify-center rounded-md bg-sky-100 text-sky-600 dark:bg-sky-950/60 dark:text-sky-400">
                <Tags aria-hidden="true" className="h-4 w-4" />
              </span>
              Specifications
            </CardTitle>
            <CardDescription>Translated attributes for the selected variant.</CardDescription>
          </div>
          <Badge variant="outline">{specifications.length}</Badge>
        </div>
      </CardHeader>
      <CardContent className="p-5 pt-0 sm:p-6 sm:pt-0">
        <dl className="grid min-w-0 gap-3 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
          {specifications.map((entry) => (
            <div
              key={`${entry.label}-${entry.value}`}
              className="min-w-0 rounded-md border bg-muted/20 p-3"
            >
              <dt className="text-xs text-muted-foreground">{entry.label}</dt>
              <dd
                className={cn(
                  "mt-1 break-words text-sm font-medium text-foreground",
                  entry.mono && "break-all font-mono text-xs",
                )}
              >
                {entry.value}
              </dd>
            </div>
          ))}
        </dl>
      </CardContent>
    </Card>
  );
}
